import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldAlert, TrendingUp, Loader2, Bug } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { getAvAnalytics, getAvStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin-only "Threats blocked" widget for the Dashboard.
 *
 * Shows scanner health, running totals over N days, a daily stacked-bar of
 * clean vs blocked scans, and the top signatures caught. Read-only.
 */
const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const formatDay = (d) => {
  try {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return d; }
};

export const AvAnalyticsCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    Promise.all([getAvAnalytics(days), getAvStatus().catch(() => null)])
      .then(([d, s]) => { setData(d); setStatus(s); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days, isAdmin]);

  if (!isAdmin) return null;

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="av-analytics-card">
        <div className="grid place-items-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const blockRate = data.total_scans ? ((data.total_blocked / data.total_scans) * 100).toFixed(1) : "0.0";
  const scannerOk = status?.reachable;

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="av-analytics-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="min-w-0">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Security · Antivirus
          </span>
          <h3 className="mt-1 font-serif text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-500" /> Threats blocked
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              data-testid="av-scanner-health"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                scannerOk
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
              }`}
              title={status.detail || ""}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${scannerOk ? "bg-emerald-500" : "bg-red-500"}`} />
              {scannerOk ? "clamd online" : "clamd offline"}
            </span>
          )}
          <div className="inline-flex rounded-full border border-border p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.days}
                data-testid={`av-range-${r.days}`}
                onClick={() => setDays(r.days)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition ${
                  days === r.days
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat data-testid="av-stat-scans" label="Total scans" value={data.total_scans.toLocaleString()} icon={TrendingUp} tone="neutral" />
        <Stat data-testid="av-stat-blocked" label="Threats blocked" value={data.total_blocked.toLocaleString()} icon={ShieldAlert} tone={data.total_blocked > 0 ? "danger" : "neutral"} />
        <Stat data-testid="av-stat-rate" label="Block rate" value={`${blockRate}%`} icon={ShieldCheck} tone="neutral" />
      </div>

      <div className="mt-6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
          Daily activity
        </p>
        <div className="h-48 w-full" data-testid="av-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.daily} barSize={days >= 30 ? 8 : 24} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
              <XAxis
                dataKey="day"
                tickFormatter={formatDay}
                fontSize={10}
                stroke="hsl(var(--muted-foreground))"
                interval={days >= 30 ? Math.ceil(days / 10) : 0}
              />
              <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 10,
                  fontSize: 12,
                }}
                labelFormatter={formatDay}
              />
              <Bar dataKey="clean" stackId="a" fill="hsl(var(--muted-foreground))" opacity={0.35} name="Clean" />
              <Bar dataKey="blocked" stackId="a" name="Blocked">
                {data.daily.map((_, i) => (
                  <Cell key={i} fill="hsl(var(--destructive))" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.top_signatures.length > 0 && (
        <div className="mt-6">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <Bug className="h-3 w-3" /> Top signatures caught
          </p>
          <ul className="rounded-xl border border-border divide-y divide-border overflow-hidden" data-testid="av-top-signatures">
            {data.top_signatures.map((s) => (
              <li key={s.signature} className="flex items-center gap-3 px-3 py-2 text-sm">
                <ShieldAlert className="h-3.5 w-3.5 text-destructive shrink-0" />
                <code className="flex-1 min-w-0 truncate font-mono text-xs">{s.signature}</code>
                <span className="rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[10px] font-bold tabular-nums">
                  ×{s.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-5 text-[11px] text-muted-foreground text-center">
        Powered by ClamAV · 3.3M+ virus signatures · signatures refresh hourly
      </p>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, tone, "data-testid": tid }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    data-testid={tid}
    className={`rounded-xl border p-3 ${
      tone === "danger" ? "border-destructive/40 bg-destructive/5" : "border-border bg-secondary/40"
    }`}
  >
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="mt-1 font-serif text-2xl font-bold">{value}</div>
  </motion.div>
);
